import { uniqueId } from 'lodash';
import { Moment } from 'moment';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { HttpResponse } from '../../../../../../../common/ajax';
import {
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../../common/global/helpers';
import Icons from '../../../../../../../common/widgets/Icons';
import { ErrorState, ErrorType } from '../../../../../models/Errors';
import { File, Timesheet } from '../../../../../models/TimeAndAttendance';
import { resources } from '../../../../../resources/resources';
import { timeAndAttendanceService } from '../../../../../services/time-and-attendance';
import { TimeAndAttendanceStep } from '../enum';

interface ITimeAndAttendanceConfirmFormProps {
  currentDate: Moment;
  setActiveStep: React.Dispatch<React.SetStateAction<TimeAndAttendanceStep>>;
  event: Timesheet | undefined;
  setTimesheet: React.Dispatch<React.SetStateAction<Timesheet[] | undefined>>;
  setFiles: React.Dispatch<React.SetStateAction<File[] | undefined>>;
  setShowAttendanceDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setErrors: Dispatch<SetStateAction<ErrorState>>;
}

export const TimeAndAttendanceConfirmForm = (
  props: ITimeAndAttendanceConfirmFormProps,
) => {
  const { t: translate } = useTranslation();

  const handleCreateEventInTimesheet = async () => {
    if (!props.event) return;

    let result: HttpResponse<boolean>;
    const newId = uniqueId();

    if (props.event.id) {
      result = await timeAndAttendanceService.updateTimesheetPresenceEvent(
        props.event.id,
        props.event,
      );
    } else {
      result = await timeAndAttendanceService.addTimesheetPresenceEvent({
        ...props.event,
        id: props.event.id || newId,
      });
    }

    if (result.hasErrors) {
      if (result.errorType === ErrorType.ValidationError) {
        setValidationErrors(result, props.setErrors);
        props.setActiveStep(TimeAndAttendanceStep.Form);
      } else {
        showErrorToast(
          translate(resources.timeAndAttendance.create.errorMessage),
        );
      }
      return;
    }

    props.setTimesheet(undefined);
    props.setFiles(undefined);
    props.setShowAttendanceDialog(false);

    const timesheetsResult = await timeAndAttendanceService.getTimesheet(
      props.currentDate,
    );

    if (!timesheetsResult.hasErrors) {
      props.setTimesheet(timesheetsResult.data);
    } else {
      showErrorToast(translate(resources.errors.generic));
    }

    const filesResult = await timeAndAttendanceService.getTimesheetFiles(
      props.currentDate,
    );

    if (!filesResult.hasErrors) {
      props.setFiles(filesResult.data);
    } else {
      showErrorToast(translate(resources.errors.generic));
    }

    showSuccessToast(
      translate(resources.timeAndAttendance.create.successMessage),
    );
  };
  return (
    <div className="taa-confirm-container">
      <div>{<Icons.HelpRed />}</div>
      {props.event && props.event.id ? (
        <h2>{translate(resources.timeAndAttendance.editConfirmation.title)}</h2>
      ) : (
        <h2>{translate(resources.timeAndAttendance.addConfirmation.title)}</h2>
      )}

      <div className="taa-confirm-actions">
        <button
          className="btn btn-solid"
          onClick={handleCreateEventInTimesheet}
        >
          <span className="btn-text">
            {props.event && props.event.id
              ? translate(resources.timeAndAttendance.save)
              : translate(resources.timeAndAttendance.add)}
          </span>
        </button>
        <button
          className="btn btn-outline"
          onClick={() => props.setActiveStep(TimeAndAttendanceStep.Form)}
        >
          <span className="btn-text">
            {translate(resources.timeAndAttendance.back)}
          </span>
        </button>
      </div>
    </div>
  );
};
